import type {
  BoxProps
} from '@chakra-ui/react'
import {
  Box,
  Button,
  Flex,
  IconButton,
  Image,
  Stack,
  Switch,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'
import type { Document, OfferPackage, PackageItemModel, PartPackage } from 'BrokerTsLib/src/dtos'
import { OfferStatusEnum } from 'BrokerTsLib/src/enums/offerStatusEnum'
import { formatPrice } from 'BrokerTsLib/src/Utils/formatPrice'
import moment from 'moment'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/context/AuthContext'

import { baseUrl } from '@/hooks'
import { LoadingOverlay, LoadingOverlayContext } from '../../../components/LoadingOverlay'
import { MenuItem, MenuItemWithSubMenu, SubMenu } from '../../../components/Menu/'
import { useCestovkoOffersContext } from '../../../context/CestovkoOffersProvider'
import { useOfferGrid } from '../../../context/OfferGridContext'
import { offerStatusColors } from '../../../data/offers/utils/offerStatusColors'
import { useGetOfferDocuments } from '../../../hooks/useDocuments'
import { useGetGroupedRisks } from '../../../hooks/useEnumTravel'
import {
  useClone,
  useGetOfferAvailableAddons,
  useSelectCalculation,
  useSetOfferIncluded,
} from '../../../hooks/useOffer'
import { useGetOfferAvailablePackageItems, useSaveOfferParts } from '../../../hooks/useOfferPackage'
import { useUpdateLastWizardStep } from '../../../hooks/useWizard'
import { Check, Duplicate, Pdf, Refresh, Settings } from '../../../icons'
import { useInsuranceParamsContext } from '../../../layouts/Wizard'
import { getLogo } from '../../../utils/companiesLogos'
import type { IBodyCell } from './columnSections'
import { DiscountCell } from './components/DiscountCell'
import { OfferPackageColumnSection } from './OfferPackageColumnSection'
import { OfferPackageSettingsForm } from './OfferPackageSettingsForm'
import { StatusBadge } from './StatusBadge'

const documentUrl = `${baseUrl}/${import.meta.env.VITE_DOCUMENT_TRAVEL_API}`

const OfferColumnSection = ({ children, sx, ...rest }: BoxProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 2,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  )
}

export interface OfferProps {
  offer: OfferPackage
  onToggle?: (included: boolean) => void
}

export const OfferCestovkoColumn = ({ offer, onToggle }: OfferProps) => {
  const navigate = useNavigate()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const { insuranceParamsId, insuranceParams, refetchInsuranceParams } = useInsuranceParamsContext()
  const {
    updateOffer,
    calculatingOffersIds,
    recalculateOffers: recalculate,
  } = useCestovkoOffersContext()
  const isRecalculating = calculatingOffersIds?.includes(offer!.offerId) ?? false
  const { setLoading } = useContext(LoadingOverlayContext)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { mutateAsync: selectOffer } = useSelectCalculation()
  const insuranceGroups = useGetGroupedRisks()
  const { mutateAsync: saveOfferParts } = useSaveOfferParts()
  const { mutateAsync: cloneOffer } = useClone(offer.offerId)
  const { mutateAsync: updateStep } = useUpdateLastWizardStep()
  const { openedColumns, onSectionTitleClick } = useOfferGrid()

  const isContracted = !!insuranceParams?.generalParams.contractNumber
  const displayId = `${offer.offerId}/${insuranceParamsId}`
  const elementId = `offer-${displayId.replace(/\//, '')}`

  const hasError = (offer.errors?.length ?? 0) > 0
  const errMsg = hasError ? offer.errors?.map((e) => e.message).join('<br/>') : ''

  let status = OfferStatusEnum.Valid
  if (hasError) status = OfferStatusEnum.Error
  else if (insuranceParams?.generalParams.selectedOfferId === offer.offerId)
    status = OfferStatusEnum.Selected

  const color = offerStatusColors[status]
  const logo = getLogo(offer.insurerId)

  const { mutateAsync: setIncluded } = useSetOfferIncluded()

  const handleToggle = (included: boolean) => {
    onToggle?.(included)

    setTimeout(() => {
      offer!.included = included
      updateOffer(offer!)
      setIncluded({ offerId: offer!.offerId!, included: included })
    }, 300)
  }

  const saveCurrentState = () => {
    setLoading(true)
    saveOfferParts(offer)
      .then(() => recalculate([offer.offerId], true))
      .finally(() => setLoading(false))
  }

  const [offerDocuments, setOfferDocuments] = useState<Document[]>([])
  const { refetch: getOfferDocuments } = useGetOfferDocuments(offer!.offerId, {
    enabled: false,
    onSuccess: (data) => {
      if (data.data?.length > 0) setOfferDocuments([...data.data])
      else setOfferDocuments([])
    },
  })
  const { data: dataOfferAvailableAddons } = useGetOfferAvailableAddons(offer!.offerId)
  const { data: dataOfferAvailablePackageItems } = useGetOfferAvailablePackageItems(offer!.offerId)
  const addons = (dataOfferAvailableAddons?.data ?? []) as PackageItemModel[]
  const packageItems = (dataOfferAvailablePackageItems?.data ?? []) as PackageItemModel[]
  const { user } = useAuth()

  // doctu nove dokumenty po kazde rekalkulaci
  useEffect(() => {
    setLoading(isRecalculating)

    if (!isRecalculating)
      getOfferDocuments() // at nebrani nacteni jinym dulezitejsim requestum
  }, [isRecalculating])

  const offerDoc = offerDocuments.find((x) => x.name === 'OfferDocument')
  const xmlDocs = offerDocuments.filter((x) => x.name !== 'OfferDocument')
  const changed = offer.parts.findIndex((x) => !!x.changed) >= 0

  const handleSettingsClick = () => {
    onOpen()
  }

  useEffect(() => {
    if (changed) saveCurrentState()
  }, [changed])

  if (!packageItems || packageItems.length == 0) return null

  return (
    <LoadingOverlay isLoading={isSubmitting}>
      <Box
        id={elementId}
        sx={{
          display: 'grid',
          gap: 'px',
          placeContent: 'stretch',
          borderLeft: '1px solid',
          borderLeftColor: 'gray.300',
          minW: 200,
        }}
      >
        {/* id, included... */}
        <OfferColumnSection
          sx={{
            px: 2,
            py: 1,
            bg: color,
          }}
        >
          <Flex sx={{ gap: 2 }}>
            <Text sx={{ fontSize: 'sm', fontWeight: 'medium' }}>{`#${displayId}`}</Text>
            {status && <StatusBadge status={status} tooltip={errMsg} />}
          </Flex>

          <Flex sx={{ alignItems: 'center', gap: 2 }}>
            <Switch
              id={`offer-toggle-${displayId}`}
              size="sm"
              isDisabled={isContracted}
              isChecked={offer?.included}
              onChange={(e) => handleToggle(e.target.checked)}
            />

            {/* <Flex>
              <IconButton bg="transparent" aria-label="Posunout doleva">
                <ChevronLeft width={3} height={3} />
              </IconButton>
              <IconButton bg="transparent" aria-label="Posunout doprava">
                <ChevronRight width={3} height={3} />
              </IconButton>
            </Flex> */}
          </Flex>
        </OfferColumnSection>

        {/* logo, sjednat button */}
        <OfferColumnSection
          sx={{
            gap: 8,
            px: 2,
            py: 0,
            height: '52px'
          }}
        >
          <Image
            src={logo}
            alt={offer.insurerName}
            width="64px"
            maxH="48px"
            style={{ mixBlendMode: 'multiply' }}
          />

          {status !== OfferStatusEnum.Error && !isContracted && !changed && (
            <Button
              colorScheme="primary"
              size="sm"
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
              loadingText="Ukládám..."
              onClick={() => {
                setIsSubmitting(true)

                selectOffer({
                  paramsId: insuranceParamsId!,
                  offerId: offer.offerId,
                })
                  .then(() => {
                    if ((insuranceParams?.generalParams.lastWizardStep ?? 3) < 4)
                      updateStep({
                        paramsId: Number(insuranceParamsId!),
                        step: 4,
                      }).then(() => refetchInsuranceParams())
                    else refetchInsuranceParams()

                    navigate('/cestovko/wizard/4/' + insuranceParamsId!)
                  })
                  .finally(() => setIsSubmitting(false))
              }}
            >
              <Check />
              Sjednat
            </Button>
          )}

          {/* {changed && (
            <Button colorScheme="blue" size="sm" onClick={saveCurrentState}>
              <Refresh />
              Přepočítat
            </Button>
          )} */}

          {status === OfferStatusEnum.Error && (
            <Tooltip
              label={
                <Stack direction={'column'}>
                  {offer?.errors.map((e, i) => {
                    return (
                      <Text key={i} variant="calcError" whiteSpace={'break-spaces'}>
                        {e.message}
                      </Text>
                    )
                  })}
                </Stack>
              }
              hasArrow
              placement="top"
            >
              <Text
                sx={{
                  color: 'red.800',
                  fontSize: 'xs',
                  fontWeight: 'semibold',
                }}
              >
                Chyba
              </Text>
            </Tooltip>
          )}
        </OfferColumnSection>

        {/* price */}
        <OfferColumnSection
          sx={{
            p: 2,
            bg: color,
          }}
        >
          <Text sx={{ fontSize: 'sm', fontWeight: 'medium' }}>{offer.title}</Text>

          <Text sx={{ fontSize: 'sm', fontWeight: 'semibold' }}>
            {changed ? '???' : formatPrice({ price: offer.price })}
          </Text>
        </OfferColumnSection>

        {/* buttons */}
        <OfferColumnSection
          sx={{
            gap: 8,
            px: 2,
          }}
        >
          <MenuItem variant="ghost" colorScheme="gray" color="gray.500" size="sm">
            &nbsp;
          </MenuItem>

          {/* <Stars /> */}

          <Flex sx={{ gap: 1, flex: 1, justifyContent: 'end' }}>
            {xmlDocs && xmlDocs.length > 0 && (
              <MenuItemWithSubMenu
                trigger="click"
                button={
                  <MenuItem variant="ghost" colorScheme="gray" color="gray.500" size="sm">
                    XML
                  </MenuItem>
                }
                subMenu={
                  <SubMenu
                    sx={{
                      maxHeight: '400px',
                      overflow: 'auto',
                      p: 2,
                      width: 'max-content',
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      bg: 'white',
                      borderRadius: 'md',
                      boxShadow: 'md',
                      listStyle: 'none',
                      zIndex: 'subMenu',
                    }}
                  >
                    <li>
                      {xmlDocs.map((doc) => {
                        return (
                          <MenuItem
                            key={doc.id}
                            target="_blank"
                            href={`${documentUrl}/GetOfferDocument?docId=${doc.id}`}
                          >
                            {doc.name} ({moment(doc.date).format('D.M.yyyy H:mm:ss')})
                          </MenuItem>
                        )
                      })}
                    </li>
                  </SubMenu>
                }
              />
            )}

            {offerDoc && (
              <Tooltip label="Stáhnout PDF nabídku pojišťovny" hasArrow placement="top">
                <IconButton
                  variant="ghost"
                  aria-label="PDF"
                  as={'a'}
                  target="_blank"
                  href={`${documentUrl}/GetOfferDocument?docId=${offerDoc.id}`}
                >
                  <Pdf color="secondary.500" />
                </IconButton>
              </Tooltip>
            )}

            {!isContracted && (
              <>
                <Tooltip label="Přepočítat nabídku" hasArrow placement="top">
                  <IconButton
                    variant="ghost"
                    aria-label="Obnovit"
                    onClick={() => {
                      recalculate([offer.offerId], true).finally(() => {
                        getOfferDocuments()
                      })
                    }}
                  >
                    <Refresh color="secondary.500" />
                  </IconButton>
                </Tooltip>

                <Tooltip label="Klonovat nabídku" hasArrow placement="top">
                  <IconButton
                    variant="ghost"
                    aria-label="Klonovat"
                    onClick={() => {
                      cloneOffer().then(({ data: offer }) => {
                        updateOffer(offer as OfferPackage)
                        recalculate([offer.offerId], true)
                      })
                    }}
                  >
                    <Duplicate color="secondary.500" />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {addons.length > 0 && (
              <Tooltip label="Další doplňková pojištění" hasArrow placement="top">
                <IconButton variant="ghost" aria-label="Nastavení" onClick={handleSettingsClick}>
                  <Settings color="secondary.500" />
                </IconButton>
              </Tooltip>
            )}
          </Flex>
        </OfferColumnSection>

        {/* risks in groups */}
        {insuranceGroups &&
          Object.keys(insuranceGroups).map((key) => {
            const items = insuranceGroups[key]
            const section = items[0]

            return (
              <OfferPackageColumnSection
                onClick={() => {
                  onSectionTitleClick(section.groupId)
                }}
                isOpened={openedColumns.includes(section.groupId)}
                key={section.groupId}
                titleBg={color}
                bodyCells={items.map((x) => {
                  return packageItems
                    .filter((pi) => pi.riskId == x.id)
                    .map((pi) => {
                      let op = offer.parts.find(
                        (op) => op.packageItem.packageItemId == pi.packageItemId,
                      )
                      if (!op) {
                        op = {
                          partId: 0,
                          riskId: x.id,
                          offerId: offer.offerId,
                          packageItem: pi,
                        } as PartPackage

                        offer.parts.push(op)
                      }

                      return {
                        id: x.id.toString(),
                        offerPart: op,
                        packageItem: pi,
                      } as IBodyCell
                    })
                })}
              />
            )
          })}

        <DiscountCell offer={offer} isDisabled={isContracted} />
      </Box>

      {
        <OfferPackageSettingsForm
          isOpen={isOpen}
          onClose={onClose}
          onSuccess={() => recalculate([offer.offerId])}
          offer={offer}
          title={`Doplňkové pojištění pro nabídku #${displayId}`}
          isReadOnly={isContracted}
        />
      }
    </LoadingOverlay>
  )
}
